import { isApolloError } from '@apollo/client';
import { ERROR_CODES } from '@shinetools/errors';

import { QesStatus } from '__generated__/GQL';
import assertEventType from 'common/bento/lib/assertEventType';
import { MachineGuards } from 'common/bento/types/MachineConfig';
import webView from 'common/webView';
import { versionIsGte } from 'common/webView/helpers/versionIsGte';
import { getApolloErrorCode } from 'features/Bento/libs/errors/utils';

import { Model } from './model';

export enum Guard {
  IsSilentError = 'isSilentError',
  IsIneligibleToQes = 'isIneligibleToQes',
  IsInvalidIdentityProviderError = 'isInvalidIdentityProviderError',
  IsIneligibleIdentityError = 'isIneligibleIdentityError',
  ShouldDoNativeFourthlineWorkflow = 'shouldDoNativeFourthlineWorkflow',
  HasNativeFourthlineWorkflowSucceeded = 'hasNativeFourthlineWorkflowSucceeded',
  HasNativeFourthlineWorkflowFailed = 'hasNativeFourthlineWorkflowFailed',
}

const guards: MachineGuards<Model, Guard> = {
  hasNativeFourthlineWorkflowFailed: (context, event) => {
    assertEventType(event, ['done.invoke.completeNativeFourthlineWorkflow']);

    return event.data.status === 'error';
  },

  hasNativeFourthlineWorkflowSucceeded: (context, event) => {
    assertEventType(event, ['done.invoke.completeNativeFourthlineWorkflow']);

    return event.data.status === 'done';
  },

  isIneligibleIdentityError: (context, event) => {
    assertEventType(event, 'error.platform.startSignature');

    const { data: error } = event;

    if (isApolloError(error)) {
      return (
        getApolloErrorCode(error) ===
        ERROR_CODES.IDENTITY_VERIFICATION_NOT_ELIGIBLE
      );
    }

    return false;
  },

  isIneligibleToQes: ({ getModuleData }) => {
    return (
      getModuleData().viewer.companyUser.qesStatus === QesStatus.Ineligible
    );
  },

  isInvalidIdentityProviderError: (context, event) => {
    assertEventType(event, 'error.platform.startSignature');

    const { data: error } = event;

    if (isApolloError(error)) {
      return (
        getApolloErrorCode(error) === ERROR_CODES.IDENTITY_VERIFICATION_INVALID
      );
    }

    return false;
  },

  isSilentError: (context, event) => {
    assertEventType(event, 'error.platform.startSignature');

    const { data: error } = event;

    if (isApolloError(error)) {
      const code = getApolloErrorCode(error);
      return code === 'APP_ERROR';
    }

    return false;
  },

  shouldDoNativeFourthlineWorkflow: (context, event) => {
    assertEventType(event, 'done.invoke.startSignature');

    if (!webView.isInWebView || !webView.config?.appVersion) {
      return false;
    }

    const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent);

    const isWebViewCompatible =
      versionIsGte(webView.config.appVersion, '1.261.0') ||
      /**
       * There was a bug in 1.260 impacting Android devices.
       */
      (isIOS && versionIsGte(webView.config.appVersion, '1.260.0'));

    return isWebViewCompatible;
  },
};

export default guards;
